import * as Yup from 'yup';

export const SMS_FORM_FORMIK_IDS = {
  MESSAGE_CONTENT: 'message_content',
  CONTACT_ID: 'contact_id',
  CONTACT: 'contact',
  PHONE_ID: 'phone_id',
  EXPOSURE_IDS: 'exposure_ids',
  SUMMARY: 'summary',
  ADD_FOLLOW_UP_REMINDER: 'add_follow_up_reminder',
};

export const MAX_SMS_LENGTH = 1400; // Twilio limit is 1600, but it includes \n\r characters, so we take a (big) safety

// The vendor's own cap, and the bound the send path enforces. MAX_SMS_LENGTH above is the advisory
// target the composer aims a typed body at; a body seeded programmatically -- the e-sign card and
// the video-call invitation both do that -- has to stay submittable up to what the server accepts.
export const MAX_SMS_BODY_LENGTH = 1600;

// The bound the composer shows and caps typing at, for a body of this length. A seeded body can
// already sit above the advisory target, and pinning the field to it there gives the user a counter
// that reads "1500/1400" with nothing invalid about the value, plus a field that refuses every
// keystroke until the value is back under 1400. Above the advisory the composer therefore shows and
// enforces the bound the schema and the send path enforce, which keeps one invariant true in both
// bands: the counter reads over its denominator only when the value is genuinely invalid.
export const getSmsCharacterLimit = (messageContent) =>
  (messageContent?.length ?? 0) > MAX_SMS_LENGTH ? MAX_SMS_BODY_LENGTH : MAX_SMS_LENGTH;

export const smsMessageValidation = Yup.object().shape({
  [SMS_FORM_FORMIK_IDS.MESSAGE_CONTENT]: Yup.string()
    .required('Required')
    .max(MAX_SMS_BODY_LENGTH, `Message must be at most ${MAX_SMS_BODY_LENGTH} characters`),
  [SMS_FORM_FORMIK_IDS.CONTACT_ID]: Yup.number().required('Required'),
  [SMS_FORM_FORMIK_IDS.CONTACT]: Yup.object()
    .test(
      'is-phone-exists',
      'Contact must contain at least one phone number',
      (contact) => contact && contact.phones && contact.phones.length > 0
    )
    .test(
      'is-smsing-allowed',
      'Sending text messages to this contact is not permitted',
      (contact) => contact && contact.is_smsing_allowed !== false
    ),
  [SMS_FORM_FORMIK_IDS.PHONE_ID]: Yup.number().required('Required'),
  [SMS_FORM_FORMIK_IDS.EXPOSURE_IDS]: Yup.array().required('Required').min(1, 'Required'),
  [SMS_FORM_FORMIK_IDS.SUMMARY]: Yup.string(),
  [SMS_FORM_FORMIK_IDS.ADD_FOLLOW_UP_REMINDER]: Yup.boolean(),
});

export const getSmsMessageValidation = ({ isCategoryRequired } = {}) =>
  smsMessageValidation.shape({
    category_id: isCategoryRequired
      ? Yup.number().required('Category is required').nullable()
      : Yup.number().nullable(),
  });

export function getAttachmentFileUrl(attachment) {
  return !attachment.document
    ? `/api/v1/communications/sms/${attachment.communication_id}/attachments/${attachment.id}/stored_file`
    : `/api/v1/claims/${attachment.document.claim_id}/documents/${attachment.document.id}`;
}

export const smsCommunicationToFormikValues = (communication) => ({
  summary: communication.summary,
  message_content: communication.message,
});

export function getSmsCommunicationSpecificIdentifier(communication) {
  // communication.specific_identifier isn't set for all of sms communications, as some time ago
  // each SMS communication had a contact
  return communication.specific_identifier || communication.contact_phone?.phone;
}
